import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
  InputAdornment,
} from "@mui/material";
import { postData, API_ENDPOINTS } from "../../services/apiService.js";

const OUTCOMES = [
  { value: "R1", label: "R1 — Full Refund to Customer" },
  { value: "R2", label: "R2 — Partial Refund to Customer" },
  { value: "R3", label: "R3 — Owner/Driver Deduction (no customer refund)" },
  { value: "R4", label: "R4 — No Action — Complaint Invalid" },
  { value: "R5", label: "R5 — Mutual Agreement" },
  { value: "R6", label: "R6 — External Escalation" },
];

const REFUND_OUTCOMES = ["R1", "R2"];
const DEDUCTION_OUTCOMES = ["R3"];

const formatINR = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

export default function ResolutionModal({
  complaintId,
  packageValue,
  cpmPremium = 0,
  cpmClaim = null,
  onClose,
  onSuccess,
}) {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // CMP-002 — payout-offset confirmation. Set when the backend returns
  // 409 PAYOUT_OFFSET_REQUIRED on an R2/R3 with an insufficient owner
  // wallet. The dialog renders the offset details and lets the admin
  // re-submit with acknowledge_payout_offset:true.
  const [offsetConfirm, setOffsetConfirm] = useState(null);

  const needsAmount =
    REFUND_OUTCOMES.includes(outcome) || DEDUCTION_OUTCOMES.includes(outcome);
  const isRefund = REFUND_OUTCOMES.includes(outcome);
  const hasPkgValue = Number.isFinite(Number(packageValue)) && Number(packageValue) > 0;
  const pkgValueNum = hasPkgValue ? Number(packageValue) : null;

  // R1 — auto-fill full package value. Locked field: admin can't edit
  // (full refund is by definition the package value).
  // R2 — leave editable but live-validate that amount < package value.
  useEffect(() => {
    if (outcome === "R1" && pkgValueNum != null) {
      setRefundAmount(String(pkgValueNum));
    }
  }, [outcome, pkgValueNum]);

  const amountNum = Number(refundAmount);
  const partialExceedsPkg =
    outcome === "R2" && hasPkgValue && refundAmount !== "" &&
    Number.isFinite(amountNum) && amountNum >= pkgValueNum;
  const partialEqualsOrLess =
    outcome === "R2" && hasPkgValue && refundAmount !== "" &&
    Number.isFinite(amountNum) && amountNum > 0 && amountNum < pkgValueNum;

  const handleSubmit = async () => {
    if (!outcome) {
      setError("Please select a resolution outcome.");
      return;
    }
    // Backend requires a non-empty resolution_note for every outcome —
    // it's the audit trail line that shows up on the customer's
    // complaint detail, so the requirement is intentional. Catch this
    // client-side so the admin sees "Resolution note is required."
    // instead of the raw "resolution_outcome and resolution_note are
    // required" backend message.
    if (!note || !note.trim()) {
      setError(
        "Resolution note is required. Summarise the decision so the customer can see why this outcome was chosen."
      );
      return;
    }
    if (needsAmount) {
      if (!refundAmount || isNaN(amountNum) || amountNum <= 0) {
        setError("Please enter a valid amount.");
        return;
      }
      // Partial refund must be strictly less than full package value —
      // otherwise the admin should pick R1 (Full Refund) instead.
      if (outcome === "R2" && hasPkgValue && amountNum >= pkgValueNum) {
        setError(
          `Partial refund must be less than the package value (${formatINR(
            pkgValueNum
          )}). Pick R1 for a full refund.`
        );
        return;
      }
    }

    await submitResolution({ acknowledgePayoutOffset: false });
  };

  // Core submit — used both by the initial Confirm Resolution click and
  // by the "Yes, net against pending payout" confirmation. The second
  // call sets acknowledge_payout_offset:true so the backend stops
  // returning 409 and applies the offset.
  const submitResolution = async ({ acknowledgePayoutOffset }) => {
    setLoading(true);
    setError("");
    try {
      const url = API_ENDPOINTS.resolveComplaint.replace(":complaint_id", complaintId);
      const payload = {
        resolution_outcome: outcome,
        resolution_note: note.trim(),
      };
      if (needsAmount) payload.refund_amount = amountNum;
      if (acknowledgePayoutOffset) payload.acknowledge_payout_offset = true;
      await postData(url, payload);
      setOffsetConfirm(null);
      onSuccess();
    } catch (err) {
      const body = err?.response?.data;
      // CMP-002 — backend signals "wallet short, ok to offset?" with
      // code:"PAYOUT_OFFSET_REQUIRED". Surface the offset details in a
      // confirm dialog instead of just showing the raw message.
      if (body?.code === "PAYOUT_OFFSET_REQUIRED") {
        setOffsetConfirm({
          shortfall: Number(body.shortfall) || 0,
          walletBalance: Number(body.walletBalance) || 0,
          requiredAmount: Number(body.requiredAmount) || 0,
          tripId: body.trip_id ?? null,
          ownerId: body.owner_id ?? null,
          message: body.message,
        });
        setError("");
      } else {
        setError(body?.message || "Resolution failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve Complaint</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Refund cap reference — shown at the top so the admin has the
            anchor number in view while picking the outcome. CPM premium
            and claim payout get their own callouts below. */}
        {hasPkgValue && (
          <Box
            sx={{
              mt: 1,
              mb: 1.5,
              p: 1.5,
              bgcolor: "grey.50",
              border: 1,
              borderColor: "grey.200",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Refund Cap (reference)
              </Typography>
              {cpmPremium > 0 && (
                <Typography variant="caption" color="text.secondary">
                  excludes CPM premium of {formatINR(cpmPremium)}
                </Typography>
              )}
            </Box>
            <Chip
              label={formatINR(pkgValueNum)}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        )}

        {/* CMP-002 — CPM claim context. The backend already 409s on a
            pending claim, but we surface the same warning here so the
            admin doesn't waste a click. */}
        {cpmClaim &&
          ["filed", "under_review", "evidence_pending"].includes(cpmClaim.status) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              CPM claim <strong>{cpmClaim.claim_ref}</strong> is still{" "}
              {cpmClaim.status?.replace("_", " ")}. Resolution will be blocked
              by the server until that claim is decided.
            </Alert>
          )}
        {cpmClaim && cpmClaim.status === "approved" &&
          Number(cpmClaim.approved_amount) > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              CPM paid <strong>{formatINR(cpmClaim.approved_amount)}</strong>{" "}
              for this package. Subtract this from any complaint refund and
              note it in the resolution.
            </Alert>
          )}

        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Resolution Outcome *</InputLabel>
          <Select
            value={outcome}
            label="Resolution Outcome *"
            onChange={(e) => {
              setOutcome(e.target.value);
              setRefundAmount("");
            }}
          >
            {OUTCOMES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {outcome === "R1" && "Full refund will be credited to customer wallet."}
            {outcome === "R2" && "Partial refund will be credited to customer wallet."}
            {outcome === "R3" && "Amount will be deducted from owner wallet."}
            {outcome === "R4" && "No financial action. Complaint closed as invalid."}
            {outcome === "R5" && "Both parties agreed. Document terms in note."}
            {outcome === "R6" && "Escalated to external authority."}
          </FormHelperText>
        </FormControl>

        {needsAmount && (
          <TextField
            label={
              isRefund
                ? "Refund Amount (₹) *"
                : "Deduction Amount (₹) *"
            }
            type="number"
            fullWidth
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            disabled={outcome === "R1"}
            error={partialExceedsPkg}
            helperText={
              outcome === "R1"
                ? `Locked to full package value (${formatINR(pkgValueNum)}).`
                : outcome === "R2" && hasPkgValue
                ? partialExceedsPkg
                  ? `Must be less than ${formatINR(pkgValueNum)} — pick R1 for full refund.`
                  : partialEqualsOrLess
                  ? `Valid. Customer will receive ${formatINR(amountNum)} (${(
                      (amountNum / pkgValueNum) *
                      100
                    ).toFixed(0)}% of package).`
                  : `Maximum ${formatINR(pkgValueNum - 1)}.`
                : " "
            }
            inputProps={{
              min: 1,
              max: outcome === "R2" && hasPkgValue ? pkgValueNum - 1 : undefined,
              step: 1,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          label="Resolution Note *"
          multiline
          rows={3}
          fullWidth
          required
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Summarise the decision, findings, or agreement..."
          error={!note.trim() && error.toLowerCase().includes("resolution note")}
          helperText={
            !note.trim()
              ? "Required — shown to the customer on their complaint detail."
              : ""
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={
            loading ||
            !outcome ||
            !note.trim() ||
            partialExceedsPkg ||
            (cpmClaim &&
              ["filed", "under_review", "evidence_pending"].includes(
                cpmClaim.status
              ))
          }
        >
          {loading ? <CircularProgress size={20} /> : "Confirm Resolution"}
        </Button>
      </DialogActions>

      {/* CMP-002 — payout-offset confirmation. Surfaces when the backend
          returned 409 PAYOUT_OFFSET_REQUIRED. The admin sees how much
          the wallet covers, how much will be earmarked against the
          owner's pending payout for the trip, and can confirm. */}
      <Dialog
        open={!!offsetConfirm}
        onClose={() => !loading && setOffsetConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Net against pending payout?</DialogTitle>
        <DialogContent>
          {offsetConfirm && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Owner wallet can't cover this resolution.
              </Alert>
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: "grey.50",
                  border: 1,
                  borderColor: "grey.200",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Required from owner
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatINR(offsetConfirm.requiredAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Owner wallet balance
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatINR(offsetConfirm.walletBalance)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.5,
                    borderTop: 1,
                    borderColor: "grey.300",
                    mt: 0.5,
                    pt: 1,
                  }}
                >
                  <Typography variant="body2" color="error" fontWeight={600}>
                    Shortfall (earmark for trip {offsetConfirm.tripId ?? "—"})
                  </Typography>
                  <Typography variant="body2" color="error" fontWeight={700}>
                    {formatINR(offsetConfirm.shortfall)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                If you confirm, the owner's wallet is debited{" "}
                <strong>{formatINR(offsetConfirm.walletBalance)}</strong> now and
                the remaining <strong>{formatINR(offsetConfirm.shortfall)}</strong>{" "}
                is earmarked against their next payout for trip{" "}
                <strong>{offsetConfirm.tripId ?? "—"}</strong>. The owner's
                withdrawals are capped at wallet balance minus this earmark
                until the next payout consumes it.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffsetConfirm(null)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() =>
              submitResolution({ acknowledgePayoutOffset: true })
            }
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              "Yes, net against pending payout"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
